"use client";

import React, { useEffect, useRef, useState } from "react";
import { Focus, Palette, Grid3x3 } from 'lucide-react';
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = '0px'
} = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const blueprintFeatures = [{
  label: "01. PERFORMANCE",
  title: "90+ PageSpeed Score",
  description: "Achieve industry-leading performance automatically. We optimize every asset so your site leaps to life with feline agility.",
  Icon: Focus
}, {
  label: "02. MIGRATION",
  title: "1-Click Site Transfer",
  description: "Move your entire ecosystem with zero downtime. Our automated migration tool handles the heavy lifting with clinical precision.",
  Icon: Palette
}, {
  label: "03. AGILITY",
  title: "AI-Powered Vitals",
  description: "Continuous monitoring and real-time adjustments ensure your core web vitals stay in the green, 24/7/365.",
  Icon: Grid3x3
}];
export default function FromLensToLayout() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="222897" id="cat-like-reflexes" className="relative bg-[#111827] py-24 md:py-32 px-6 overflow-hidden dark">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-5xl h-[500px] bg-primary/10 blur-[120px] rounded-full pointer-events-none" aria-hidden="true" />

      <div ref={ref} className={`max-w-7xl mx-auto transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="relative bg-[#f78f1e] rounded-xl shadow-2xl overflow-hidden px-8 py-16 md:px-12 md:py-20 lg:p-24">
          {}
          <div className="absolute inset-0 bg-white/[0.03] pointer-events-none mix-blend-overlay" aria-hidden="true" />
          
          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-16 md:gap-12 lg:gap-20">
            {blueprintFeatures.map((feature, i) => <div key={i} data-index={i} className="flex flex-col items-center text-center group" style={{
            transitionDelay: `${i * 150}ms`
          }}>
                {}
                <div className="mb-8 p-5 bg-white/10 rounded-2xl transition-all duration-500 group-hover:scale-110 group-hover:bg-white/20 group-hover:shadow-xl">
                  <feature.Icon className="size-10 text-white stroke-[1.5px]" aria-hidden="true" />
                </div>

                {}
                <span className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-white/70 mb-4 block">
                  {feature.label}
                </span>

                {}
                <h3 className="font-sans text-2xl lg:text-3xl font-bold mb-5 text-white tracking-tight">
                  {feature.title}
                </h3>

                {}
                <p className="font-sans text-sm md:text-base text-white/90 leading-relaxed max-w-xs md:max-w-none">
                  {feature.description}
                </p>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}